// this file is intended for moving feature flag definitions to
// when feature flags become fairly static. The side effect of
// leaving feature flags in feature creature after the feature
// is finalized is that we have unintended side effects and
// re-renders due to retrieval of feature flags client side
// re-triggering hooks.

import { getMembership } from '~/techstyle-shared/react-accounts';
import { getFeature, loadFeatures } from '~/techstyle-shared/react-features';
import { getSession } from '~/techstyle-shared/redux-core';

import { staticFeatureValidators } from '../constants/featureFlags';

import { getLDFlagWithCtx } from './launchDarkly';
import { getGender } from './useGender';

const DEFAULT_OPTIONS = {
  includeValue: false,
};

/**
 * @description Helper function to get the enabled state of a feature flag. This will use
 * static feature validators if available, fetch the flag from LD when the `ldClient` is available
 * using the `getLDFlagWithCtx` utility, and finally fetch the flag from feature creature when the
 * other options are not available.
 *
 * There is a third `options` parameter that is used to include the value of the feature flag as well
 * as the enabled state. When `{ includeValue: true }` is passed, the return value will be an object
 * with the following shape: { enabled, data }.
 */
export default async function getHasFeature(
  ctx,
  handle,
  options = DEFAULT_OPTIONS
) {
  const { includeValue } = options;
  const session = getSession(ctx.store.getState());
  const membership = getMembership(ctx.store.getState());
  const tld = ctx.store.getState().domain.tld;
  const { gender } = getGender(ctx.store.getState());
  const validator = staticFeatureValidators[handle];
  const hasStaticFeature =
    typeof validator !== 'undefined' && handle in staticFeatureValidators;

  if (hasStaticFeature) {
    const result =
      typeof validator === 'function'
        ? validator({
            session,
            membership,
            gender,
            tld,
          })
        : validator;

    if (includeValue) {
      return {
        enabled: result,
        data: result,
      };
    }

    return result;
  }

  if (ctx.ldClient) {
    const ldFlag = await getLDFlagWithCtx({
      ctx,
      flag: handle,
    });

    if (ldFlag !== null && ldFlag !== undefined) {
      if (typeof ldFlag === 'boolean') {
        if (includeValue) {
          return {
            enabled: ldFlag,
            data: ldFlag,
          };
        }

        return ldFlag;
      } else {
        if (includeValue) {
          return {
            enabled: true,
            data: ldFlag,
          };
        }

        return true;
      }
    }
  }

  // make a call to getFeature to get from feature creature
  await ctx.store.dispatch(loadFeatures(handle));
  const { isEnabled, data } = getFeature(ctx.store.getState(), handle);

  if (includeValue) {
    return {
      enabled: isEnabled,
      data,
    };
  }

  return isEnabled;
}
